/* eslint-disable no-case-declarations */
import moment from 'moment';

import {
    SEARCH_SET_TYPE_SORTING,
    SEARCH_UPDATE_QUERY,
    SEARCH_REPLACE_FILTER_INPUTS,
    SEARCH_UPDATE_FILTERS_APPLIED,
} from '../actions/actionTypes/search-action-types';
import { SearchLimitPerPage } from '../utils/search';

/**
 * Default-valued shape for state.search.filters.
 *
 * Hoisted so it can be re-used by SEARCH:REPLACE_FILTER_INPUTS (the
 * wipe-and-replace action used by the natural-language search thunk) and
 * by callers that need to know which dict keys carry boolean-dict shape
 * (eventTypes / ballTypes / etc.) vs. flat-value shape. Exported for the
 * natural-search thunk and its tests.
 */
export const INITIAL_FILTERS = {
    sportTypes: {},
    type: 'players',
    utrMin: 1,
    utrMax: 16,
    gender: '',
    genders: {},
    distance: '',
    location: null,
    locationInputValue: '',
    utrType: 'verified',
    utrTeamType: 'singles',
    utrReliability: null,
    hsGradYears: {},
    tours: {},
    eventRegions: {},
    eventTypes: {},
    eventTags: {},
    eventUtrTypes: {},
    eventTeamTypes: {},
    utrFitPosition: 6,
    ballTypes: {},
};

const initialState = {
    mode: 'search', // modes: 'search' (redirects to result), 'lookup' (stores result for other components for submitting score, invitation, etc),
    lookupType: 'players',
    isLoading: false,
    pageLoadedOnce: false,
    clearGlobalSearch: false,
    isExpanded: false,
    allowInvite: false,
    displayFilters: true,
    gotoSearchResults: true,
    prismicContent: null,
    filters: { ...INITIAL_FILTERS },
    sportTypeId: 1,
    filterInputs: {},
    players: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    events: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    flexLeagues: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    teamLeagues: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    proTennisTour: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    paidHits: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    juniorCircuits: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    camps: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    juniorRegionals: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    collegeCircuits: {
        total: 0,
        totalAllowed: 0,
        top: 40,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: true,
        },
    },
    clubs: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: false,
        },
    },
    colleges: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: false,
        },
    },
    highSchools: {
        total: 0,
        totalAllowed: 0,
        top: 10,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: false,
        },
    },
    adultTeamClubs: {
        total: 0,
        totalAllowed: 0,
        top: SearchLimitPerPage,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: false,
        },
    },
    paidHitClubs: {
        total: 0,
        totalAllowed: 0,
        top: SearchLimitPerPage,
        skip: 0,
        hits: [],
        sort: {
            field: '',
            asc: false,
        },
    },
    geocoded: null,
};

function app(state = initialState, action) {
    switch (action.type) {
        case 'SEARCH:UPDATE_SPORT_TYPES':
            const { tennis, pickleball } = action.data;
            return {
                ...state,
                filters: {
                    ...state.filters,
                    sportTypes: {
                        tennis,
                        pickleball,
                    },

                },
                filterInputs: {
                    ...state.filterInputs,

                    sportTypes: {
                        tennis,
                        pickleball,
                    },
                },

            };

        case 'SEARCH:SET_SEARCH_MODE':
            const { mode, lookupType, lookUpParams, lookupCount, lookup, displayText, allowInvite, displayFilters, gotoSearchResults, sportTypeId } = action.params;
            return {
                ...state,
                mode,
                lookupType,
                lookUpParams,
                lookupCount,
                lookup,
                displayText,
                allowInvite,
                displayFilters: (displayFilters === null || displayFilters === undefined) ? state.displayFilters : displayFilters,
                gotoSearchResults: (gotoSearchResults === null || gotoSearchResults === undefined) ? state.gotoSearchResults : gotoSearchResults,
                sportTypeId,

            };

        case 'SEARCH:SET_SEARCHPAGE_TYPE':
            const { searchPageType } = action;
            return {
                ...state,
                searchPageType,
            };

        case 'SEARCH:SET_TYPE_FILTER': {
            const { lat, lng } = state.geocoded || {};
            const typeChange = state.filters && state.filters.type !== action.filter;
            const prevCountryName = state.filters?.locationCountryName ?? '';
            const prevStateName = state.filters?.locationStateName ?? '';
            const locationCountryName = typeChange ? '' : prevCountryName;
            const locationStateName = typeChange ? '' : prevStateName;
            return {
                ...state,
                // listAll: true, //replaces 'explore'
                filters: {
                    ...state.filters,
                    type: action.filter,
                    ...(lat ? { lat } : {}),
                    ...(lng ? { lng } : {}),
                    locationCountryName,
                    locationStateName,
                },
                filterInputs: {
                    ...state.filters,
                    type: action.filter,
                    ...(lat ? { lat } : {}),
                    ...(lng ? { lng } : {}),
                    locationCountryName,
                    locationStateName,
                },
            };
        }
        case SEARCH_SET_TYPE_SORTING: {
            const { type, field, direction } = action.sort;
            let asc;
            if (direction) {
                asc = direction === 'asc';
            } else {
                asc = !state[type].sort.field || state[type].sort.field !== field ? state[type].sort.asc : !state[type].sort.asc;
            }

            return {
                ...state,
                [type]: {
                    ...state[type],
                    sort: {
                        field,
                        asc,
                    },
                },
            };
        }
        case 'SEARCH:SET_GEO_LOCATION': {
            return {
                ...state,
                geocoded: action.geocoded,
            };
        }

        case 'SEARCH:STORE_RESULTS': {
            const { query } = action;
            const { players, clubs, events, colleges, highSchools } = action.results;
            const skipIncremented = {
                players: {
                    ...players,
                    skip: 10,
                    sort: {
                        field: '',
                        asc: true,
                    },
                },
                events: {
                    ...events,
                     skip: 40,
                    sort: {
                        field: '',
                        asc: true,
                    },
                },
                clubs: {
                    ...clubs,
                    skip: 10,
                    sort: {
                        field: '',
                        asc: true,
                    },
                },
                colleges: {
                    ...colleges,
                    skip: 10,
                    sort: {
                        field: '',
                        asc: true,
                    },
                },
                highSchools: {
                    ...highSchools,
                    skip: 10,
                    sort: {
                        field: '',
                        asc: true,
                    },
                },
            };
            return {
                ...state,
                query,
                filters: state.filters,
                ...skipIncremented,
                pageLoadedOnce: true,
                isExpanded: false,
            };
        }
        case SEARCH_UPDATE_QUERY: {
            const { query } = action;
            return {
                ...state,
                query,
            };
        }
        case 'SEARCH:FETCH_SUCCESS': {
            let newState;
            const { typeFilter } = action;
            if (typeFilter) {
                newState = {
                    ...state,
                    filters: state.filters,
                    [typeFilter]: {
                        ...state[typeFilter],
                        // skip: state[typeFilter].skip + 10,
                         skip: 40,
                        ...action.data,
                    },
                    pageLoadedOnce: true,
                    isLoading: false,
                };
            } else {
                const { players, clubs, events, highSchools, colleges, flexLeagues, teamLeagues, proTennisTour, adultTeamClubs, paidHits, paidHitClubs, juniorCircuits, collegeCircuits, juniorRegionals, camps } = action.data;
                newState = {
                    ...state,
                    filters: state.filters,
                    filterInputs: {
                        ...state.filters,
                    },
                    camps: {
                        ...camps,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    players: {
                        ...players,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    events: {
                        ...events,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    flexLeagues: {
                        ...state.flexLeagues,
                        ...flexLeagues,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    teamLeagues: {
                        ...state.teamLeagues,
                        ...teamLeagues,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    proTennisTour: {
                        ...state.proTennisTour,
                        ...proTennisTour,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    clubs: {
                        ...clubs,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    colleges: {
                        ...colleges,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    highSchools: {
                        ...highSchools,
                        skip: 10,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    adultTeamClubs: {
                        ...state.adultTeamClubs,
                        ...adultTeamClubs,
                        skip: SearchLimitPerPage,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    paidHits: {
                        ...state.paidHits,
                        ...paidHits,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    paidHitClubs: {
                        ...state.paidHitClubs,
                        ...paidHitClubs,
                        skip: SearchLimitPerPage,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    juniorCircuits: {
                        ...state.juniorCircuits,
                        ...juniorCircuits,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    collegeCircuits: {
                        ...state.collegeCircuits,
                        ...collegeCircuits,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    juniorRegionals: {
                        ...state.juniorRegionals,
                        ...juniorRegionals,
                         skip: 40,
                        sort: {
                            field: '',
                            asc: true,
                        },
                    },
                    pageLoadedOnce: true,
                    isLoading: false,
                };
            }

            return newState;
        }
        case 'SEARCH:CHANGE_FILTER_INPUTS': {
            // update value of filter input fields before applying changes
            const { filters } = action;
            if (filters.startDate) {
                filters.startDate = moment(filters.startDate);
            }
            if (filters.endDate) {
                filters.endDate = moment(filters.endDate);
            }
            return {
                ...state,
                filterInputs: {
                    ...state.filterInputs,
                    ...filters,
                },
            };
        }

        case SEARCH_REPLACE_FILTER_INPUTS: {
            // Overlay onto INITIAL_FILTERS so system defaults survive the
            // wipe; clone the payload so callers' refs are not mutated.
            const { filters: incoming } = action;
            const filters = { ...incoming };
            if (filters.startDate) {
                filters.startDate = moment(filters.startDate);
            }
            if (filters.endDate) {
                filters.endDate = moment(filters.endDate);
            }
            return {
                ...state,
                filterInputs: { ...INITIAL_FILTERS, ...filters },
            };
        }

        case SEARCH_UPDATE_FILTERS_APPLIED: {
            // apply the filter updates (from filterInputs)
            return {
                ...state,
                filters: {
                    ...state.filterInputs,
                },
            };
        }

        case 'SEARCH:UNDO_FILTER_CHANGE': {
            // undo changes of filter input fields from active filters
            return {
                ...state,
                filterInputs: {
                    ...state.filters,
                },
            };
        }

        case 'SEARCH:RESET_TYPE_FILTER': {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    type: '',
                },
            };
        }

        case 'SEARCH:RESET_LOCATION': {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    distance: '',
                },
            };
        }

        case 'SEARCH:SET_LOADING_FLAG':
        case 'SEARCH:FETCH_REQUEST':
        case 'SEARCH:TYPEAHEAD_FETCH_REQUEST': {
            return {
                ...state,
                isLoading: true,
            };
        }

        case 'SEARCH:LOAD_PLAYERS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.players = {
                    ...state.players,
                    total: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_CLUBS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.clubs = {
                    ...state.clubs,
                    total: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_EVENTS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.events = {
                    ...state.events,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_FLEX_LEAGUES_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.flexLeagues = {
                    ...state.flexLeagues,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_TEAM_LEAGUES_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.teamLeagues = {
                    ...state.teamLeagues,
                    total: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_PRO_TENNIS_TOUR_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.proTennisTour = {
                    ...state.proTennisTour,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_JUNIOR_CIRCUITS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.juniorCircuits = {
                    ...state.juniorCircuits,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_JUNIOR_REGIONALS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.juniorRegionals = {
                    ...state.juniorRegionals,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_CAMPS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.camps = {
                    ...state.camps,
                    total: 0,
                    totalAllowed: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_COLLEGE_CIRCUITS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.collegeCircuits = {
                    ...state.collegeCircuits,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_PAID_HITS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.paidHits = {
                    ...state.paidHits,
                    total: 0,
                    top: 40,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_HIGHSCHOOLS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.highSchools = {
                    ...state.highSchools,
                    total: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_COLLEGES_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.colleges = {
                    ...state.colleges,
                    total: 0,
                    top: 10,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_ADULT_TEAM_CLUBS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.adultTeamClubs = {
                    ...state.adultTeamClubs,
                    total: 0,
                    top: SearchLimitPerPage,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }
        case 'SEARCH:LOAD_PAID_HIT_CLUBS_REQUEST': {
            const newState = {
                ...state,
                isLoading: true,
                isLoadingType: true,
            };
            if (action.newSearch) {
                newState.paidHitClubs = {
                    ...state.paidHitClubs,
                    total: 0,
                    top: SearchLimitPerPage,
                    skip: 0,
                    hits: [],
                };
            }
            return newState;
        }

        case 'SEARCH:LOAD_PLAYERS_FAILURE':
        case 'SEARCH:LOAD_CLUBS_FAILURE':
        case 'SEARCH:LOAD_EVENTS_FAILURE':
        case 'SEARCH:LOAD_FLEX_LEAGUES_FAILURE':
        case 'SEARCH:LOAD_TEAM_LEAGUES_FAILURE':
        case 'SEARCH:LOAD_PRO_TENNIS_TOUR_FAILURE':
        case 'SEARCH:LOAD_JUNIOR_CIRCUITS_FAILURE':
        case 'SEARCH:LOAD_JUNIOR_REGIONALS_FAILURE':
        case 'SEARCH:LOAD_COLLEGE_CIRCUITS_FAILURE':
        case 'SEARCH:LOAD_HIGHSCHOOLS_FAILURE':
        case 'SEARCH:LOAD_COLLEGES_FAILURE':
        case 'SEARCH:LOAD_ADULT_TEAM_CLUBS_FAILURE':
        case 'SEARCH:LOAD_CAMPS_FAILURE':
        case 'SEARCH:FETCH_FAILURE':
            return {
                ...state,
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_PLAYERS_SUCCESS':
            return {
                ...state,
                players: {
                    ...state.players,
                    hits: [
                        ...(action.skip ? state.players.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_CLUBS_SUCCESS':
            return {
                ...state,
                clubs: {
                    ...state.clubs,
                    hits: [
                        ...(action.skip ? state.clubs.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_EVENTS_SUCCESS':
            return {
                ...state,
                events: {
                    ...state.events,
                    hits: [
                        ...(action.skip ? state.events.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_FLEX_LEAGUES_SUCCESS':
            return {
                ...state,
                flexLeagues: {
                    ...state.flexLeagues,
                    hits: [
                        ...(action.skip ? state.flexLeagues.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_TEAM_LEAGUES_SUCCESS':
            return {
                ...state,
                teamLeagues: {
                    ...state.teamLeagues,
                    hits: [
                        ...(action.skip ? state.teamLeagues.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_PRO_TENNIS_TOUR_SUCCESS':
            return {
                ...state,
                proTennisTour: {
                    ...state.proTennisTour,
                    hits: [
                        ...(action.skip ? state.proTennisTour.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_JUNIOR_CIRCUITS_SUCCESS':
            return {
                ...state,
                juniorCircuits: {
                    ...state.juniorCircuits,
                    hits: [
                        ...(action.skip ? state.juniorCircuits.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };
        case 'SEARCH:LOAD_JUNIOR_REGIONALS_SUCCESS':
            return {
                ...state,
                juniorRegionals: {
                    ...state.juniorRegionals,
                    hits: [
                        ...(action.skip ? state.juniorRegionals.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };
        case 'SEARCH:LOAD_CAMPS_SUCCESS':
            return {
                ...state,
                camps: {
                    ...state.camps,
                    hits: [
                        ...(action.skip ? state.camps.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                    totalAllowed: action.data.totalAllowed || action.data.total || 0,
                },
                isLoading: false,
                isLoadingType: false,
            };
        case 'SEARCH:LOAD_COLLEGE_CIRCUITS_SUCCESS':
            return {
                ...state,
                collegeCircuits: {
                    ...state.collegeCircuits,
                    hits: [
                        ...(action.skip ? state.collegeCircuits.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };
        case 'SEARCH:LOAD_HIGHSCHOOLS_SUCCESS':
            return {
                ...state,
                highSchools: {
                    ...state.highSchools,
                    hits: [
                        ...(action.skip ? state.highSchools.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_COLLEGES_SUCCESS':
            return {
                ...state,
                colleges: {
                    ...state.colleges,
                    hits: [
                        ...(action.skip ? state.colleges.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_ADULT_TEAM_CLUBS_SUCCESS':
            return {
                ...state,
                adultTeamClubs: {
                    ...state.adultTeamClubs,
                    hits: [
                        ...(action.skip ? state.adultTeamClubs.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_PAID_HITS_SUCCESS':
            return {
                ...state,
                paidHits: {
                    ...state.paidHits,
                    hits: [
                        ...(action.skip ? state.paidHits.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:LOAD_PAID_HIT_CLUBS_SUCCESS':
            return {
                ...state,
                paidHitClubs: {
                    ...state.paidHitClubs,
                    hits: [
                        ...(action.skip ? state.paidHitClubs.hits : []),
                        ...action.data.hits,
                    ],
                    skip: action.skip,
                    total: action.data.total,
                },
                isLoading: false,
                isLoadingType: false,
            };

        case 'SEARCH:TYPEAHEAD_FETCH_SUCCESS':
            const { value: options } = action.data;
            options.forEach((item) => {
                // eslint-disable-next-line no-param-reassign
                item.id = item.Id;
                // eslint-disable-next-line no-param-reassign
                item.label = item.DisplayName;
            });

            return {
                ...state,
                isLoading: false,
                options,
            };

        case 'SEARCH:TYPEAHEAD_FETCH_FAILURE':
            return {
                ...state,
                clearGlobalSearch: true,
            };

        case 'SEARCH:CLEAR_GLOBAL_SEARCH':
            return {
                ...state,
                isLoading: false,
            };

        case 'SEARCH:EXPAND_SEARCH':
            return {
                ...state,
                isExpanded: action.isExpanded,
            };

        case 'SEARCH:UPDATE_QUERY_PARAMS':
            return {
                ...state,
                updateQueryParams: action.updateQueryParams,
            };

        case 'SEARCH:FETCH_PRISMIC_CONTENT_REQUEST':
            return {
                ...state,
                isLoading: true,
                prismicContentLoaded: false,
            };

        case 'SEARCH:FETCH_PRISMIC_CONTENT_SUCCESS':
            return {
                ...state,
                prismicContent: action.data,
                isLoading: false,
                prismicContentLoaded: true,
            };

        case 'SEARCH:FETCH_PRISMIC_CONTENT_FAILURE':
            return {
                ...state,
                isLoading: false,
                prismicContentLoaded: false,
            };

        default:
            return state;
    }
}

export default app;
